// Sync conflict service — Admin variant.
//
// G2.24: Admin port of the POS sync service. Only the conflict-related
// methods (list / detail / resolve) are exposed because:
//   - Push is cashier-side and inherently device-local.
//   - Pull is cashier-side bootstrap and not interesting at the platform.
//   - Conflict resolution IS the surface that platform staff need.
//
// Every method accepts an optional `tenantId` argument. When supplied AND
// the JWT user has role >= MODERATOR, the backend's `?tenant_id=` query
// parameter routes the read against that tenant instead of the JWT's own
// tenant. The platform-role gate is enforced server-side (see
// services/api/src/sync/sync.controller.ts :: resolveEffectiveTenant); we
// just thread the value through. Tenant-level OWNER/MANAGER admins call
// these methods without the override and get their own tenant by default.
//
// Traceability: FR-SYNC-013, US-BILL-019, AGENTS.md ("Conflicts are queued
// for Manager/Owner review").

import { apiClient, apiClientPaginated } from '@/lib/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface SyncConflict {
  id: string;
  entity_type: string;
  client_id: string;
  resolution: 'PENDING' | 'CLIENT_WINS' | 'SERVER_WINS' | 'MERGED' | 'DISCARDED';
  created_at: string;
}

export interface SyncConflictDetail extends SyncConflict {
  client_version: Record<string, unknown>;
  server_version: Record<string, unknown>;
  device_id: string | null;
  resolved_by?: string;
  resolved_at?: string;
  resolution_note?: string;
  /** VERSION or REJECTED_CREATE (sync plan Phase 3). */
  kind?: 'VERSION' | 'REJECTED_CREATE';
  /** The outcomes the API offers for this conflict. */
  allowed_resolutions?: string[];
  merged_version_suggestion?: Record<string, unknown> | null;
  resolution_error?: string | null;
}

export interface ResolveConflictResult {
  id: string;
  resolution: string;
  resolved_by: string;
  resolved_at: string;
  note: string | null;
}

export interface ConflictListParams {
  resolution?: string;
  page?: number;
  limit?: number;
  /** G2.24 platform override — leave undefined for tenant users. */
  tenantId?: string;
  /** Column to sort by across the whole dataset (created_at, entity_type, resolution). */
  sort?: string;
  /** Sort direction; defaults to desc server-side. */
  order?: 'asc' | 'desc';
}

export interface ResolveConflictPayload {
  resolution: string;
  note?: string;
  /** MERGED only: the confirmed record to apply. */
  merged_version?: Record<string, unknown>;
  /** G2.24 platform override. */
  tenantId?: string;
}

/**
 * G2.26: Per-tenant aggregate row for the Sync Health Overview widget on
 * the platform dashboard. Returned by `GET /sync/conflict-summary`.
 * Mirrors the backend `SyncConflictSummaryRow` shape in
 * services/api/src/sync/sync.repository.ts.
 */
export interface SyncConflictSummaryRow {
  tenant_id: string;
  tenant_name: string;
  pending_count: number;
  last_conflict_at: string;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function buildConflictQuery(params: {
  resolution?: string;
  page?: number;
  limit?: number;
  tenantId?: string;
  sort?: string;
  order?: 'asc' | 'desc';
}): string {
  const qs = new URLSearchParams();
  if (params.resolution) qs.set('resolution', params.resolution);
  if (params.page != null) qs.set('page', String(params.page));
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.tenantId) qs.set('tenant_id', params.tenantId);
  if (params.sort) qs.set('sort', params.sort);
  if (params.order) qs.set('order', params.order);
  const q = qs.toString();
  return q ? `?${q}` : '';
}

// ─── Service ────────────────────────────────────────────────────────────────

export const adminSyncService = {
  listConflicts(params: ConflictListParams = {}) {
    return apiClientPaginated<SyncConflict[]>(
      `/sync/conflicts${buildConflictQuery(params)}`,
    );
  },

  getConflict(id: string, tenantId?: string) {
    const q = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : '';
    return apiClient<SyncConflictDetail>(`/sync/conflicts/${id}${q}`);
  },

  resolveConflict(id: string, payload: ResolveConflictPayload) {
    const { tenantId, ...body } = payload;
    const q = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : '';
    return apiClient<ResolveConflictResult>(`/sync/conflicts/${id}/resolve${q}`, {
      method: 'POST',
      body,
    });
  },

  /**
   * Bulk-resolve multiple conflicts with the same resolution.
   * Max 50 per request. Returns per-conflict results.
   */
  bulkResolveConflicts(payload: {
    conflict_ids: string[];
    resolution: string;
    note?: string;
    tenantId?: string;
  }) {
    const { tenantId, ...body } = payload;
    const q = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : '';
    return apiClient<{
      results: Array<{ conflict_id: string; status: 'resolved' | 'failed'; error?: string }>;
      resolved: number;
      failed: number;
    }>(`/sync/conflicts/bulk-resolve${q}`, {
      method: 'POST',
      body,
    });
  },

  /**
   * G2.26: Cross-tenant pending-conflict summary for the platform
   * Sync Health Overview widget. Platform-only — the backend enforces
   * `@Roles('MODERATOR')` so tenant-level OWNER/MANAGER get 403.
   */
  getConflictSummary(limit?: number) {
    const q = limit != null ? `?limit=${limit}` : '';
    return apiClient<{ data: SyncConflictSummaryRow[] }>(
      `/sync/conflict-summary${q}`,
    );
  },
};
